import React, { useRef } from "react";
import { Button, Typography } from "@material-ui/core";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "@/langs/useTranslation";
import {
  scheduleDeviceStatusApi,
  getDeviceScheduleApi,
  unbindDevicesScheduleTemplatesApi
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import { toast } from "@/apis/request";
import HasPermission from "@/components/commons/HasPermission";
import ScheduleTaskType, {
  getScheduleTaskType
} from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskType";
import ScheduleType from "@/containers/Schedule/CreateTemplate/TaskCreator/ScheduleType";
import TaskTime from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskTime";
import ScheduleTaskRange from "@/containers/Schedule/CreateTemplate/TaskCreator/TaskRange";
import { ScheduleChipStatus } from "@/components/FormFields/ScheduleStatus";
import { ExcuteChipStatus } from "@/components/FormFields/ExcuteStatus";
import { dateInNormal } from "./datetimeHandlers";
import { TargetTypeChipStatus } from "@/components/FormFields/TargetType";

export const DeviceScheduledListDatatable = React.memo(
  ({ deviceId, className, style, footer, children, onCheckAll, onLoaded }) => {
    const { t } = useTranslation();
    const tableRef = useRef(null);

    const handleReloadDataTable = () => {
      tableRef.current?.reload();
    };
    const doEnable = async (ids, enable) => {
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await scheduleDeviceStatusApi({
        status: enable ? 0 : 1,
        schedule_device_maps: {
          // key 是设备id, value 是对应的 schedule ids
          [deviceId]: ids
        }
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    };

    const onDisable = row => {
      modalConfirm({
        title: t({ defaultMessage: "禁用排程" }),
        content: t(
          {
            defaultMessage: "是否确认禁用（{name}）该排程？"
          },
          { name: getScheduleTaskType(row.task_type, row.ext) }
        ),
        onConfirm: () => {
          return doEnable(row.schedule_id, false);
        }
      });
    };

    const onEnable = row => {
      modalConfirm({
        title: t({ defaultMessage: "启用排程" }),
        content: t(
          {
            defaultMessage: "是否确认启用（{name}）该排程？"
          },
          { name: getScheduleTaskType(row.task_type, row.ext) }
        ),
        onConfirm: () => {
          return doEnable(row.schedule_id, true);
        }
      });
    };

    const doDelete = async ids => {
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await unbindDevicesScheduleTemplatesApi({
        targetType: 0, // 0设备 1门店 2标签
        device_template_maps: {
          // key 是设备id, value 是对应的 template ids
          [deviceId]: ids
        }
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    };

    const onDelete = row => {
      modalConfirm({
        title: t({ defaultMessage: "删除排程模板" }),
        content: t(
          {
            defaultMessage: "是否确认删除（{name}）该排程模板？"
          },
          { name: row.schedule_template_name }
        ),
        onConfirm: () => {
          return doDelete(row.schedule_template_id);
        }
      });
    };

    const columns = [
      {
        data: "schedule_template_name",
        title: t({ defaultMessage: "排程模板" })
      },
      {
        data: "task_type",
        title: t({ defaultMessage: "排程任务" }),
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleTaskType data={row} />, td);
        }
      },
      {
        data: "schedule_type",
        title: t({ defaultMessage: "循环模式" }),
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleType data={row} />, td);
        }
      },
      {
        data: "template_target_type",
        title: t({ defaultMessage: "套用类型" }),
        searchable: false,
        className: "center",
        createdCell: (td, data) => {
          ReactRender(<TargetTypeChipStatus status={data} />, td);
        }
      },
      {
        data: "task_type",
        title: t({ defaultMessage: "执行时间" }),
        createdCell: (td, data, row) => {
          ReactRender(<TaskTime data={row} />, td);
        }
      },
      {
        data: "task_type",
        title: t({ defaultMessage: "执行期间" }),
        createdCell: (td, data, row) => {
          ReactRender(<ScheduleTaskRange data={row} />, td);
        }
      },
      {
        data: "schedule_status",
        title: t({ defaultMessage: "排程状态" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(<ScheduleChipStatus status={data} />, td);
        }
      },
      {
        data: "latest_executed_status",
        title: t({ defaultMessage: "最后执行状态" }),
        className: "center",
        createdCell: (td, data) => {
          ReactRender(<ExcuteChipStatus status={data} />, td);
        }
      },
      {
        data: "latest_executed_at",
        title: t({ defaultMessage: "最后执行时间" }),
        render: (data, row) => {
          return dateInNormal(data, true);
        }
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        // className: "operation2",
        createdCell: (td, data, row) => {
          ReactRender(
            <>
              <HasPermission permission="schedule_task_list_task_enable_disable">
                {row.schedule_status === 0 ? (
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => onDisable(row)}
                  >
                    {t({ defaultMessage: "关闭" })}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => onEnable(row)}
                  >
                    {t({ defaultMessage: "开启" })}
                  </Button>
                )}
              </HasPermission>
            </>,
            td
          );
        }
      }
      // {
      //   data: "schedule_template_name",
      //   title: "",
      //   width: 57,
      //   className: "sticky right operation",
      //   createdCell: (td, data, row) => {
      //     ReactRender(
      //       <HasPermission permission="schedule_task_list_task_enable_disable">
      //         <Button
      //           size="small"
      //           color="secondary"
      //           onClick={() => onDelete(row)}
      //         >
      //           {t({ defaultMessage: "删除" })}
      //         </Button>
      //       </HasPermission>,
      //       td
      //     );
      //   }
      // }
    ];

    const ajax = (data, callback) => {
      return getDeviceScheduleApi({
        // ...data,
        device_id: deviceId,
        searching: data.search?.value ?? "",
        page: data.page,
        pageSize: data.pageSize
      }).then(res => {
        callback({
          data: res.data,
          draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    return (
      <DataTable
        ref={tableRef}
        searching
        columns={columns}
        rowsGroup={["schedule_template_name:name"]}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        showCheckAll={false}
        onCheckAll={onCheckAll}
        onLoaded={onLoaded}
        autoLoad
      >
        {children}
      </DataTable>
    );
  }
);
